
export default function DesignSystem() {
  const components = [
    { name: 'Component Library', description: 'Reusable UI components for forms, tables, cards, and navigation', icon: 'ri-layout-grid-line' },
    { name: 'Interaction Patterns', description: 'Standardized behaviors for filtering, sorting, and data entry', icon: 'ri-cursor-line' },
    { name: 'Design Tokens', description: 'Centralized color, typography, spacing, and elevation values', icon: 'ri-palette-line' }
  ];

  return (
    <section className="py-20" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FEF7F6 100%)' }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#182423', color: 'white' }}>
              System Design
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#182423' }}>
              Design System & Scalability
            </h2>
            <p className="text-lg text-gray-600">
              A comprehensive design system ensuring visual and interaction consistency across all seven modules.
            </p>
          </div>
          
          {/* Color Palette Preview */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-6">Color Palette</h4>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="h-24 rounded-2xl mb-3" style={{ backgroundColor: '#F16051' }}></div>
                <p className="text-sm font-bold text-gray-900">Primary</p>
                <p className="text-xs text-gray-500">#F16051</p>
              </div>
              <div className="flex-1">
                <div className="h-24 rounded-2xl mb-3" style={{ backgroundColor: '#182423' }}></div>
                <p className="text-sm font-bold text-gray-900">Secondary</p>
                <p className="text-xs text-gray-500">#182423</p>
              </div>
              <div className="flex-1">
                <div className="h-24 rounded-2xl mb-3 bg-gray-100 border border-gray-200"></div>
                <p className="text-sm font-bold text-gray-900">Neutral</p>
                <p className="text-xs text-gray-500">#F3F4F6</p>
              </div>
            </div>
          </div>
        </div>

        {/* Component Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {components.map((component, index) => (
            <div 
              key={index} 
              className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: index % 2 === 0 ? '#F16051' : '#182423' }}
              >
                <i className={`${component.icon} text-white text-2xl`}></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{component.name}</h4>
              <p className="text-gray-600 text-sm">{component.description}</p>
            </div>
          ))}
        </div>

        {/* Pattern Library Preview */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #18242310 0%, transparent 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F16051' }}></div>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#182423' }}></div>
              <div className="w-3 h-3 rounded-full bg-gray-300"></div>
              <span className="text-sm text-gray-500 ml-2">Pattern Library</span>
            </div>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-4 gap-4 mb-8">
              {['Button', 'Input', 'Card', 'Modal', 'Table', 'Dropdown', 'Badge', 'Alert'].map((item, index) => (
                <div 
                  key={index} 
                  className="aspect-square rounded-xl flex items-center justify-center text-sm font-medium"
                  style={{ 
                    backgroundColor: index % 3 === 0 ? '#F1605120' : index % 3 === 1 ? '#18242320' : '#F3F4F6',
                    color: index % 3 === 0 ? '#F16051' : index % 3 === 1 ? '#182423' : '#6B7280'
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Scalability Considerations</h4>
              <p className="text-gray-600 leading-relaxed mb-4">
                The design system was built to accommodate future modules and features without requiring fundamental redesigns. Component APIs were designed for flexibility, allowing new functionality to be added through configuration rather than custom development.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This approach reduced design and development time for new features while maintaining consistency. As the platform evolved, the design system served as a single source of truth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
